/**
 * Builds allowed next-step candidates for Owen (rules + LLM picker).
 */
import { getQuestionText } from '../config/recommendationQuestions';
import { getVisibleV1TriageQuestions } from '../config/v1/triage';
import { getVisibleV1ProductFields } from '../config/v1/fields';
import {
  checkV1TriageExit,
  routeV1TriageComplete,
  isV1TriageComplete,
} from './v1/triageRouter';
import {
  getVisibleRecommendationQuestions,
  hasCoreLegacyAnswers,
  hasLocationAnswers,
  LEGACY_CORE_QUESTION_ORDER,
  LEGACY_REFINEMENT_PRIORITY,
} from './owenQuestionRouter';
import { calculateRecommendations } from './recommendationEngine';

const isAnswered = (answers, id) => {
  if (id === 'propertyLocation') return hasLocationAnswers(answers);
  const v = answers[id];
  return v !== undefined && v !== null && String(v).trim() !== '';
};

/** @typedef {{ type: string, id: string, question?: string, productIds?: string[] }} ApplyNextStep */

/** @returns {ApplyNextStep[]} */
export function buildLegacyAllowedNextSteps(answers) {
  const programs = calculateRecommendations(answers);
  const visible = getVisibleRecommendationQuestions(answers);
  const unanswered = visible.filter((q) => !isAnswered(answers, q.id));
  const purpose = answers.purchaseOrRefinance;
  const steps = [];

  const pushQuestion = (q) => {
    if (!q || steps.some((s) => s.id === q.id)) return;
    steps.push({
      type: 'question',
      id: q.id,
      question: getQuestionText(q, purpose),
    });
  };

  LEGACY_CORE_QUESTION_ORDER.forEach((id) => {
    pushQuestion(unanswered.find((q) => q.id === id));
  });

  if (!hasCoreLegacyAnswers(answers)) {
    return steps.length ? steps : [{ type: 'complete', id: 'done' }];
  }

  if (programs.length >= 1) {
    LEGACY_REFINEMENT_PRIORITY.forEach((id) => {
      pushQuestion(unanswered.find((q) => q.id === id));
    });
    unanswered.forEach((q) => pushQuestion(q));
  }

  if (unanswered.length === 0) {
    if (programs.length === 0) {
      steps.push({ type: 'complete', id: 'no_match' });
    } else if (programs.length === 1) {
      steps.push({
        type: 'complete',
        id: 'single_program',
        question: programs[0].name || programs[0].programName,
      });
    } else {
      steps.push({
        type: 'question',
        id: '__legacy_pick__',
        question: 'Which program should we prepare first?',
      });
    }
  }

  return steps.length ? steps : [{ type: 'complete', id: 'done' }];
}

/** @returns {ApplyNextStep[]} */
export function buildV1TriageAllowedNextSteps(triageAnswers) {
  const exitId = checkV1TriageExit(triageAnswers);
  if (exitId) {
    return [{ type: 'exit', id: exitId }];
  }

  if (!isV1TriageComplete(triageAnswers)) {
    return getVisibleV1TriageQuestions(triageAnswers)
      .filter((q) => !isAnswered(triageAnswers, q.id))
      .map((q) => ({
        type: 'question',
        id: q.id,
        question: q.question,
      }));
  }

  const route = routeV1TriageComplete(triageAnswers);
  if (route.type === 'exit') {
    return [{ type: 'exit', id: route.exitId }];
  }
  if (route.type === 'choose') {
    return [{
      type: 'choose_product',
      id: '__choose_product__',
      question: 'Which preparation path fits you best?',
      productIds: route.productIds,
    }];
  }
  if (route.type === 'product') {
    return [{ type: 'product', id: route.productId }];
  }
  return [{ type: 'exit', id: 'other' }];
}

/** @returns {ApplyNextStep[]} */
export function buildV1FieldAllowedNextSteps(productId, fieldAnswers) {
  const fields = getVisibleV1ProductFields(productId, fieldAnswers);
  const unanswered = fields.filter((q) => !isAnswered(fieldAnswers, q.id));
  if (unanswered.length) {
    return unanswered.map((q) => ({
      type: 'question',
      id: q.id,
      question: q.question,
    }));
  }
  return [{ type: 'documents', id: 'documents' }];
}

/** @returns {ApplyNextStep[]} */
export function buildV1ChooseAllowedNextSteps(productIds) {
  return [{
    type: 'choose_product',
    id: '__choose_product__',
    question: 'Which preparation path fits you best?',
    productIds,
  }];
}

/** Rule-based default when picking from allowed list */
export function defaultStepFromAllowed(steps) {
  return steps[0] || { type: 'complete', id: 'done' };
}

export function programsForAi(recommendedProducts) {
  return (recommendedProducts || []).slice(0, 6).map((p) => ({
    id: p.id || p.programId,
    name: p.name || p.programName,
  }));
}
